// frontend/src/features/portfolio-diversification-optimizer-pro/store/useDiversificationProFormStore.ts
//
// Persisted Zustand store for the PRO Diversification Optimizer builder
// (PortfolioProBuilder). Every edit -- holdings, weights, lookback horizon,
// box constraints, presets, auto-fit -- overwrites the single saved snapshot
// in localStorage, so leaving the page (or refreshing) and coming back
// restores the user's very latest configuration instead of the default book.
//
// Design notes (same pattern as portfolio-drift-auditor-pro/store):
//   - Bounds live here (FORM_BOUNDS, MIN/MAX_HOLDINGS, TICKER_RE) and are
//     shared with the builder's sliders, so the UI range and the hydration
//     clamp can never drift apart. They mirror PortfolioProRequest in
//     backend/app/domain/portfolio_diversification_optimizer_pro/schemas.py
//     (lookback_years 1..10, min_weight 0..0.25, max_weight <= 1.0, 2..50
//     holdings, ticker regex).
//   - Hydration is defensive by construction: the shared storage adapter
//     swallows unavailable-storage / quota / corrupt-JSON errors, and `merge`
//     re-validates every field of the (untrusted) persisted payload, clamping
//     to the current bounds and falling back to defaults per field.
//   - `validateProForm` / `buildProRequest` are the single source of truth for
//     box-constraint feasibility + payload shaping, shared by the builder's
//     submit and the dashboard's auto-run on mount.

import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  clampToBound,
  createSafeLocalStorage,
  isFiniteNumber,
  isRecord,
  type Bound,
} from "../../../shared/utils/persistedForm";
import type { HoldingRow, PortfolioProRequest } from "../types";

export const DIVERSIFICATION_PRO_FORM_STORAGE_KEY = "forge:diversification-optimizer-pro-form";
const STORAGE_VERSION = 1;

export const MIN_HOLDINGS = 2;
export const MAX_HOLDINGS = 50;
export const TICKER_RE = /^[A-Z0-9^][A-Z0-9.\-=]{0,19}$/;
const MAX_LABEL_LENGTH = 120;

export const FORM_BOUNDS = {
  lookbackYears: { min: 1, max: 10, step: 1 },
  minWeightPct: { min: 0, max: 25, step: 0.5 },
  maxWeightPct: { min: 10, max: 100, step: 1 },
} as const satisfies Record<string, Bound>;

export const DEFAULT_HOLDINGS: readonly HoldingRow[] = [
  { ticker: "AAPL", name: "Apple Inc.", assetClass: "Equity", rawWeight: 25 },
  { ticker: "MSFT", name: "Microsoft Corp.", assetClass: "Equity", rawWeight: 25 },
  { ticker: "NVDA", name: "NVIDIA Corp.", assetClass: "Equity", rawWeight: 20 },
  { ticker: "GLD", name: "SPDR Gold Shares", assetClass: "Commodity", rawWeight: 15 },
  { ticker: "TLT", name: "iShares 20+ Year Treasury Bond", assetClass: "Fixed Income", rawWeight: 15 },
];
export const DEFAULT_LOOKBACK_YEARS = 3;
const DEFAULT_MIN_WEIGHT_PCT = 2;
const DEFAULT_MAX_WEIGHT_PCT = 45;

export interface DiversificationProFormValues {
  holdings: HoldingRow[];
  lookbackYears: number;
  minWeightPct: number;
  maxWeightPct: number;
}

type ScalarField = Exclude<keyof DiversificationProFormValues, "holdings">;
type HoldingsUpdate = HoldingRow[] | ((prev: HoldingRow[]) => HoldingRow[]);

interface DiversificationProFormStore extends DiversificationProFormValues {
  setField: <K extends ScalarField>(key: K, value: DiversificationProFormValues[K]) => void;
  setHoldings: (update: HoldingsUpdate) => void;
  loadPreset: (holdings: readonly HoldingRow[]) => void;
  autoFitBounds: () => void;
  reset: () => void;
}

// Always-feasible institutional cap for N holdings (N * cap >= 100%).
const fitMaxWeightPct = (n: number): number => Math.min(100, Math.max(35, Math.ceil(200 / n)));

const defaultValues = (): DiversificationProFormValues => ({
  holdings: DEFAULT_HOLDINGS.map((h) => ({ ...h })),
  lookbackYears: DEFAULT_LOOKBACK_YEARS,
  minWeightPct: DEFAULT_MIN_WEIGHT_PCT,
  maxWeightPct: DEFAULT_MAX_WEIGHT_PCT,
});

// -- Feasibility + payload shaping -------------------------------------------
type ConstraintInputs = Pick<DiversificationProFormValues, "holdings" | "minWeightPct" | "maxWeightPct">;

export interface ConstraintValidation {
  valid: boolean;
  message: string | null;
}

const positiveWeight = (w: number): number => (Number.isFinite(w) && w > 0 ? w : 0);

export const totalRawWeightOf = (holdings: readonly HoldingRow[]): number =>
  holdings.reduce((acc, h) => acc + positiveWeight(h.rawWeight), 0);

export const validateProForm = ({
  holdings,
  minWeightPct,
  maxWeightPct,
}: ConstraintInputs): ConstraintValidation => {
  const n = holdings.length;
  const minW = minWeightPct / 100;
  const maxW = maxWeightPct / 100;

  if (n < MIN_HOLDINGS) {
    return { valid: false, message: "Add at least 2 distinct holdings." };
  }
  if (n > MAX_HOLDINGS) {
    return { valid: false, message: `Remove holdings: at most ${MAX_HOLDINGS} are supported.` };
  }
  if (minW >= maxW) {
    return {
      valid: false,
      message: "Minimum weight floor must be strictly less than the maximum weight cap.",
    };
  }
  if (n * minW > 1.0 + 1e-6) {
    const maxAllowedFloor = Math.floor((100 / n) * 10) / 10;
    return {
      valid: false,
      message: `Infeasible floor: ${n} assets × ${minWeightPct}% exceeds 100% (max floor for ${n} assets is ${maxAllowedFloor}%).`,
    };
  }
  if (n * maxW < 1.0 - 1e-6) {
    const minRequiredCap = Math.ceil((100 / n) * 10) / 10;
    return {
      valid: false,
      message: `Infeasible cap: ${n} assets × ${maxWeightPct}% cannot reach 100% (min cap for ${n} assets is ${minRequiredCap}%).`,
    };
  }
  if (totalRawWeightOf(holdings) <= 0) {
    return { valid: false, message: "Total portfolio weight must be greater than zero." };
  }
  return { valid: true, message: null };
};

// Returns the API payload, or null when the box constraints are infeasible.
export const buildProRequest = (
  values: Pick<DiversificationProFormValues, "holdings" | "lookbackYears" | "minWeightPct" | "maxWeightPct">
): PortfolioProRequest | null => {
  if (!validateProForm(values).valid) return null;
  const weights: Record<string, number> = {};
  for (const h of values.holdings) {
    if (positiveWeight(h.rawWeight) > 0) weights[h.ticker] = h.rawWeight;
  }
  return {
    weights,
    lookback_years: values.lookbackYears,
    min_weight: Number((values.minWeightPct / 100).toFixed(4)),
    max_weight: Number((values.maxWeightPct / 100).toFixed(4)),
  };
};

// -- Defensive hydration ------------------------------------------------------
const optionalLabel = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim().slice(0, MAX_LABEL_LENGTH) : undefined;

const sanitizeHoldings = (raw: unknown): HoldingRow[] | null => {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<string>();
  const holdings: HoldingRow[] = [];
  for (const r of raw) {
    if (holdings.length >= MAX_HOLDINGS) break;
    if (!isRecord(r) || typeof r.ticker !== "string") continue;
    const ticker = r.ticker.trim().toUpperCase();
    // Rows are keyed by ticker in the builder, so drop invalid / duplicate ones.
    if (!TICKER_RE.test(ticker) || seen.has(ticker)) continue;
    seen.add(ticker);
    const row: HoldingRow = {
      ticker,
      rawWeight: isFiniteNumber(r.rawWeight) && r.rawWeight >= 0 ? r.rawWeight : 0,
    };
    const name = optionalLabel(r.name);
    const assetClass = optionalLabel(r.assetClass);
    if (name) row.name = name;
    if (assetClass) row.assetClass = assetClass;
    holdings.push(row);
  }
  return holdings.length >= MIN_HOLDINGS ? holdings : null;
};

export const sanitizeFormValues = (raw: unknown): DiversificationProFormValues => {
  const d = defaultValues();
  if (!isRecord(raw)) return d;
  const B = FORM_BOUNDS;
  return {
    holdings: sanitizeHoldings(raw.holdings) ?? d.holdings,
    lookbackYears: clampToBound(raw.lookbackYears, d.lookbackYears, B.lookbackYears),
    minWeightPct: clampToBound(raw.minWeightPct, d.minWeightPct, B.minWeightPct),
    maxWeightPct: clampToBound(raw.maxWeightPct, d.maxWeightPct, B.maxWeightPct),
  };
};

export const useDiversificationProFormStore = create<DiversificationProFormStore>()(
  persist(
    (set) => ({
      ...defaultValues(),
      setField: (key, value) => set({ [key]: value } as Partial<DiversificationProFormValues>),
      setHoldings: (update) =>
        set((s) => ({ holdings: typeof update === "function" ? update(s.holdings) : update })),
      loadPreset: (holdings) =>
        set({
          holdings: holdings.map((h) => ({ ...h })),
          minWeightPct: DEFAULT_MIN_WEIGHT_PCT,
          maxWeightPct: fitMaxWeightPct(holdings.length),
        }),
      autoFitBounds: () =>
        set((s) => {
          const n = Math.max(s.holdings.length, MIN_HOLDINGS);
          return { minWeightPct: Math.min(5, Math.floor(50 / n)), maxWeightPct: fitMaxWeightPct(n) };
        }),
      reset: () => set(defaultValues()),
    }),
    {
      name: DIVERSIFICATION_PRO_FORM_STORAGE_KEY,
      version: STORAGE_VERSION,
      storage: createSafeLocalStorage<DiversificationProFormValues>(),
      // Persist ONLY the serialisable form values, never the actions.
      partialize: (s): DiversificationProFormValues => ({
        holdings: s.holdings,
        lookbackYears: s.lookbackYears,
        minWeightPct: s.minWeightPct,
        maxWeightPct: s.maxWeightPct,
      }),
      // Older / foreign schema versions are re-validated field-by-field.
      migrate: (persisted) => sanitizeFormValues(persisted),
      merge: (persisted, current) => ({ ...current, ...sanitizeFormValues(persisted) }),
    }
  )
);
